import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n, bilingual } from "../lib/i18n";import { HeatBanner } from "../components/HeatBanner";
import { Users, ClockClockwise, Warning, ChartLine, Hammer } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="bg-white border border-stone-200 p-6 shadow-sm shadow-stone-900/5"
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-medium tracking-wide uppercase text-stone-500">{label}</div>
          <div
            className="mt-2 text-4xl font-black tracking-tighter"
            style={{ fontFamily: "'Fraunces', 'Inter Tight', serif" }}
          >
            {value}
          </div>
        </div>
        <div className={`w-10 h-10 ${accent} border border-stone-200 flex items-center justify-center`}>
          <Icon weight="bold" size={22} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [summary, setSummary] = useState(null);
  const [today, setToday] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobMap, setJobMap] = useState({});
  const [activeSort, setActiveSort] = useState("ranch");
  const [openRequests, setOpenRequests] = useState(0);
  const activeEmployees = employees.filter((e) => e.active).length;

  const load = async () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const end = now.toISOString().slice(0, 10);
    try {
      if (user?.role !== "foreman") {
        const { data } = await api.get(`/reports/summary?start_date=${start}&end_date=${end}`);
        setSummary(data);
      }
      const { data: list } = await api.get(`/time-entries?start_date=${end}&end_date=${end}`);
      setToday(list);
      const { data: emps } = await api.get(`/employees`);
      setEmployees(emps);
      const { data: jobsData } = await api.get(`/jobcodes`);
      const jm = {};
      jobsData.forEach((j) => { jm[j.job_code || j.code] = j; });
      setJobMap(jm);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    if (user?.role === "admin") {
      api.get("/work-requests").then(({ data }) => setOpenRequests(data.filter((r) => r.status === "open").length)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1
            className="text-4xl sm:text-5xl font-semibold tracking-tight"
            style={{ fontFamily: "'Fraunces', 'Inter Tight', serif" }}
          >
            {user?.role === "foreman" ? t("dash.todays_crew") : t("dash.operations")}
          </h1>
          <p className="text-sm font-medium tracking-wide uppercase text-stone-500">
            {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </p>
        </div>
      </div>

      <HeatBanner />

      {user?.role === "admin" && openRequests > 0 && (
        <Link to="/worklist?tab=client_requests" data-testid="client-requests-banner"
          className="flex items-center gap-3 bg-amber-50 border-2 border-amber-400 p-4 hover:border-amber-500 transition-colors">
          <div className="w-10 h-10 bg-amber-400 flex items-center justify-center shrink-0">
            <Hammer weight="bold" size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-amber-900">
              {openRequests} new client work request{openRequests === 1 ? "" : "s"} waiting for review
            </div>
            <div className="text-xs text-amber-700">Approve them to send the work to your supervisors' Work List</div>
          </div>
          <span data-testid="client-requests-badge" className="bg-amber-500 text-white font-black text-lg px-3.5 py-1.5">{openRequests}</span>
        </Link>
      )}

      {(() => {
        const active = today.filter((e) => !e.clock_out);
        const taskOf = (e) => jobMap[e.job_code]?.job || jobMap[e.job_code]?.description || e.job_code || "—";
        const onBreak = (e) => (e.breaks || []).some((b) => !b.end);
        const sorters = {
          ranch: (a, b) => String(a.ranch_name || "").localeCompare(b.ranch_name || "") || taskOf(a).localeCompare(taskOf(b)),
          task: (a, b) => taskOf(a).localeCompare(taskOf(b)) || String(a.ranch_name || "").localeCompare(b.ranch_name || ""),
          employee: (a, b) => String(a.employee_name || "").localeCompare(b.employee_name || ""),
        };
        const activeSorted = [...active].sort(sorters[activeSort] || sorters.ranch);
        const tally = (keyFn) => {
          const m = {};
          active.forEach((e) => { const k = keyFn(e) || "—"; m[k] = (m[k] || 0) + 1; });
          return Object.entries(m).sort((a, b) => b[1] - a[1]);
        };
        const byTask = tally(taskOf);
        const byRanch = tally((e) => e.ranch_name);
        return (
          <section data-testid="clocked-in-section">
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-2xl font-bold tracking-tight uppercase" style={{ fontFamily: "'Fraunces', serif" }}>On the Clock</h2>
              <span data-testid="clocked-in-count" className="inline-flex items-center gap-1.5 bg-[#1F3D34] text-white text-xs font-bold uppercase tracking-wide px-3 py-1">
                <span className="w-2 h-2 rounded-full bg-[#7C9673] animate-pulse" /> {active.length} working now
              </span>
              <div className="inline-flex border border-stone-200 ml-auto" data-testid="active-sort">
                <span className="px-2 self-center text-[10px] font-bold uppercase tracking-wide text-stone-400">Sort</span>
                {[["ranch", "Ranch"], ["task", "Job Task"], ["employee", "Employee"]].map(([k, label]) => (
                  <button key={k} data-testid={`active-sort-${k}`} onClick={() => setActiveSort(k)}
                    className={`h-8 px-3 text-xs font-bold uppercase tracking-wide ${activeSort === k ? "bg-[#1F3D34] text-white" : "bg-white text-stone-600"}`}>{label}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-white border border-stone-200 shadow-sm shadow-stone-900/5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b-2 border-stone-300 bg-stone-50">
                    <tr className="text-left tracking-wide text-xs">
                      <th className="p-3">Employee</th><th className="p-3">Job Task</th>
                      <th className="p-3">Ranch</th><th className="p-3 font-mono">Since</th><th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.length === 0 && (<tr><td colSpan={5} className="p-6 text-center text-stone-500 font-medium uppercase text-xs">Nobody clocked in right now</td></tr>)}
                    {activeSorted.map((e) => (
                      <tr key={e.id} className="border-b border-stone-200" data-testid={`active-row-${e.id}`}>
                        <td className="p-3 font-bold">{e.employee_name}</td>
                        <td className="p-3 text-xs">{taskOf(e)}</td>
                        <td className="p-3 text-xs">{e.ranch_name || "—"}</td>
                        <td className="p-3 font-mono text-xs">{e.clock_in ? new Date(e.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                        <td className="p-3">
                          {onBreak(e)
                            ? <span className="bg-[#D8C7A1] border border-stone-300 text-xs font-bold uppercase px-2 py-0.5">On Break</span>
                            : <span className="text-[#16A34A] text-xs font-bold uppercase">● Working</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-4">
                <div className="bg-white border border-stone-200 p-4 shadow-sm shadow-stone-900/5" data-testid="summary-by-task">
                  <div className="text-xs font-bold uppercase tracking-wide text-stone-500 mb-2">Tasks In Progress</div>
                  {byTask.length === 0 && <div className="text-xs text-stone-400">—</div>}
                  {byTask.map(([k, n]) => (
                    <div key={k} className="flex justify-between text-sm border-b border-stone-100 py-1">
                      <span className="truncate pr-2">{k}</span><span className="font-mono font-bold">{n}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-white border border-stone-200 p-4 shadow-sm shadow-stone-900/5" data-testid="summary-by-ranch">
                  <div className="text-xs font-bold uppercase tracking-wide text-stone-500 mb-2">Crews By Ranch</div>
                  {byRanch.length === 0 && <div className="text-xs text-stone-400">—</div>}
                  {byRanch.map(([k, n]) => (
                    <div key={k} className="flex justify-between text-sm border-b border-stone-100 py-1">
                      <span className="truncate pr-2">{k}</span><span className="font-mono font-bold">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <StatCard label={t("dash.regular_hrs")} value={summary.total_regular} icon={ClockClockwise} accent="bg-white" />
          <StatCard label={t("dash.overtime_hrs")} value={summary.total_overtime} icon={ChartLine} accent="bg-[#D8C7A1]" />
          <StatCard label={t("dash.doubletime_hrs")} value={summary.total_doubletime} icon={Warning} accent="bg-[#1F3D34] text-white" />
          <StatCard label={t("dash.active_employees")} value={activeEmployees} icon={Users} accent="bg-white" />
        </div>
      )}

      {summary && (summary.meal_penalties > 0 || summary.rest_penalties > 0) && (
        <div className="bg-[#D8C7A1] border border-stone-200 p-4 shadow-sm shadow-stone-900/5" data-testid="compliance-warning">
          <div className="flex items-center gap-2 font-medium tracking-wide uppercase text-sm">
            <Warning weight="bold" /> {t("dash.penalties")}
          </div>
          <div className="mt-1 text-sm font-medium">
            {t("dash.meal_penalties")}: <b>{summary.meal_penalties}</b> · {t("dash.rest_penalties")}: <b>{summary.rest_penalties}</b>
          </div>
        </div>
      )}

      {summary && summary.by_category && summary.by_category.length > 0 && (
        <section data-testid="category-totals">
          <h2 className="text-2xl font-bold tracking-tight uppercase mb-1" style={{ fontFamily: "'Fraunces', serif" }}>
            {t("dash.category_totals")}
          </h2>
          <p className="text-xs font-medium tracking-wide uppercase text-stone-500 mb-3">{t("dash.cat_hours_only")}</p>
          <div className="bg-white border border-stone-200 shadow-sm shadow-stone-900/5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b-2 border-stone-300 bg-stone-50">
                <tr className="text-left tracking-wide text-xs">
                  <th className="p-3">Category</th>
                  <th className="p-3 text-right">Reg</th>
                  <th className="p-3 text-right">OT</th>
                  <th className="p-3 text-right">DT</th>
                  <th className="p-3 text-right">Total Hrs</th>
                  <th className="p-3 text-right">Entries</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_category.map((c) => (
                  <tr key={c.category} className="border-b border-stone-200" data-testid={`category-total-${c.category}`}>
                    <td className="p-3 font-bold text-xs uppercase tracking-wide">
                      {c.category_code ? `${c.category_code} · ` : ""}{bilingual(c.category, c.category_es, lang)}
                    </td>
                    <td className="p-3 font-mono text-right">{c.regular_hours}</td>
                    <td className="p-3 font-mono text-right">{c.overtime_hours}</td>
                    <td className="p-3 font-mono text-right">{c.doubletime_hours}</td>
                    <td className="p-3 font-mono text-right font-bold">{c.total_hours}</td>
                    <td className="p-3 font-mono text-right text-stone-500">{c.entries_count}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-stone-300 bg-stone-50" data-testid="category-total-grand">
                  <td className="p-3 font-black text-xs uppercase tracking-wide">Total</td>
                  <td className="p-3 font-mono text-right font-bold">{summary.total_regular}</td>
                  <td className="p-3 font-mono text-right font-bold">{summary.total_overtime}</td>
                  <td className="p-3 font-mono text-right font-bold">{summary.total_doubletime}</td>
                  <td className="p-3 font-mono text-right font-black">{summary.total_hours}</td>
                  <td className="p-3 font-mono text-right text-stone-500">{summary.entries_count}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <h2 className="text-2xl font-bold tracking-tight uppercase mb-3" style={{ fontFamily: "'Fraunces', serif" }}>
          Today&apos;s Entries
        </h2>
        <div className="bg-white border border-stone-200 shadow-sm shadow-stone-900/5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b-2 border-stone-300 bg-stone-50">
              <tr className="text-left tracking-wide text-xs">
                <th className="p-3">Category</th>
                <th className="p-3">Ranch</th>
                <th className="p-3">Employee</th>
                <th className="p-3">Job</th>
                <th className="p-3 font-mono">Clock In</th>
                <th className="p-3 font-mono">Clock Out</th>
                <th className="p-3">Reg</th>
                <th className="p-3">OT</th>
                <th className="p-3">DT</th>
                <th className="p-3">Flags</th>
              </tr>
            </thead>
            <tbody>
              {today.length === 0 && (
                <tr><td colSpan={10} className="p-6 text-center text-stone-500 font-medium tracking-wide uppercase text-xs">{t("dash.no_entries")}</td></tr>
              )}
              {[...today]
                .sort((a, b) => {
                  const ca = jobMap[a.job_code]?.category || "";
                  const cb = jobMap[b.job_code]?.category || "";
                  if (!ca && cb) return 1;
                  if (ca && !cb) return -1;
                  return (
                    ca.localeCompare(cb) ||
                    String(a.job_code ?? "").localeCompare(String(b.job_code ?? "")) ||
                    String(a.employee_name ?? "").localeCompare(String(b.employee_name ?? ""))
                  );
                })
                .map((e) => (
                <tr key={e.id} className="border-b border-stone-200" data-testid={`entry-row-${e.id}`}>
                  <td className="p-3 text-xs font-bold uppercase tracking-wide">
                    {jobMap[e.job_code]?.category
                      ? `${jobMap[e.job_code]?.category_code ? jobMap[e.job_code].category_code + " · " : ""}${jobMap[e.job_code].category}`
                      : "—"}
                  </td>
                  <td className="p-3 text-xs">{e.ranch_name || "—"}</td>
                  <td className="p-3 font-bold">{e.employee_name}</td>
                  <td className="p-3 font-mono text-xs">{e.job_code || "—"}</td>
                  <td className="p-3 font-mono">{e.clock_in ? new Date(e.clock_in).toLocaleTimeString() : "—"}</td>
                  <td className="p-3 font-mono">{e.clock_out ? new Date(e.clock_out).toLocaleTimeString() : <span className="text-[#16A34A] font-bold">ACTIVE</span>}</td>
                  <td className="p-3 font-mono">{e.regular_hours}</td>
                  <td className="p-3 font-mono">{e.overtime_hours}</td>
                  <td className="p-3 font-mono">{e.doubletime_hours}</td>
                  <td className="p-3 text-xs">
                    {e.meal_break_penalty && <span className="bg-red-100 border border-red-600 text-red-700 px-2 py-0.5 mr-1 font-bold uppercase">Meal</span>}
                    {e.rest_break_penalty && <span className="bg-red-100 border border-red-600 text-red-700 px-2 py-0.5 mr-1 font-bold uppercase">Rest</span>}
                    {e.seventh_day && <span className="bg-[#D8C7A1] border border-stone-300 px-2 py-0.5 font-bold uppercase">7th Day</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
